import { isEmpty } from 'lodash-es';
import { useCallback, useEffect, useMemo } from 'react';
import { translateEventToSaleAccordionItem } from 'src/components/Accordions';
import { useAccordionItem } from 'src/components/Events/EventPage/hooks/useAccordionItem';
import { SidePanelToolbar } from 'src/components/Events/EventPage/SidePanel/SidePanelToolbar';
import { ActivePosEntityProvider } from 'src/contexts/ActivePosEntityContext';
import { ActiveSortTableColumnProvider } from 'src/contexts/ActiveSortTableColumnContext';
import { useAppContext } from 'src/contexts/AppContext';
import { BulkEditHubContextProvider } from 'src/contexts/BulkEditHubContext';
import { CatalogDataContextProvider } from 'src/contexts/CatalogDataContext';
import { CatalogMultiSelectionContextProvider } from 'src/contexts/CatalogMultiSelectionContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { Content, FormatContent } from 'src/contexts/ContentContext';
import { DialogProvider } from 'src/contexts/DialogContext/DialogContext';
import {
  FilterQueryContextProvider,
  useFilterQueryContext,
} from 'src/contexts/FilterQueryContext';
import {
  MultiSelectionContextProvider,
  useMultiSelectionContext,
} from 'src/contexts/MultiSelectionContext';
import { PosSpinner } from 'src/core/POS/PosSpinner';
import { useUserHasTableTotalsFeature } from 'src/hooks/useUserHasFeature';
import { getSalesForEvents } from 'src/navigations/Routes/Sales';
import { NoData } from 'src/tables/Table';
import { SalesTableColumnId } from 'src/utils/columns/sales/salesColumnUtils.types';
import { ContentId } from 'src/utils/constants/contentId';
import { FormatContentId } from 'src/utils/constants/formatContentId';
import { DefaultSaleQuery, EmptySaleQuery } from 'src/utils/eventQueryUtils';
import { transformData } from 'src/utils/eventWithDataUtils';
import { createAggregatedSale } from 'src/utils/saleUtils';
import { SectionType } from 'src/utils/types/sectionType';
import {
  ActionOutboxEntityType,
  SaleClient,
  SaleDetails,
  SaleQuery,
  SectionInfo,
  UserSetting,
} from 'src/WebApiController';

import * as styles from '../../Events/EventPage/EventPage.css';
import { SaleSideTableFilterBar } from '../SaleFilterBar/SaleSideTableFilterBar';

const Sales = ({
  viagVirtId,
  selectedSections,
  queryInput,
}: {
  viagVirtId: string;
  selectedSections?: SectionInfo[];
  queryInput?: Partial<Pick<SaleQuery, 'purchaseOrderIds' | 'listingIds'>>;
}) => {
  const hasTableTotalsFeature = useUserHasTableTotalsFeature(
    ActionOutboxEntityType.Sale
  );

  const { isLoading, isItemsLoading, accordionItemProps } =
    useAccordionItem(viagVirtId);

  const { setSelectionMode } = useMultiSelectionContext();
  useEffect(() => {
    setSelectionMode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viagVirtId]);

  const { setFilterQuery, filterQuery } = useFilterQueryContext<SaleQuery>();
  useEffect(() => {
    setFilterQuery({
      ...filterQuery,
      ...queryInput,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryInput, setFilterQuery]);

  const hasSelectedEntities =
    (queryInput?.listingIds && queryInput?.listingIds?.length > 0) ||
    (queryInput?.purchaseOrderIds && queryInput?.purchaseOrderIds?.length > 0);

  const { entities } = accordionItemProps || {};
  const sales = entities?.sales;

  const hasNoFilter = !selectedSections || isEmpty(selectedSections);

  const filteredEventSales = useMemo(() => {
    if (hasNoFilter) {
      return sales ?? null;
    }
    const sectionIds = new Set(selectedSections.map(({ id }) => id));
    const sectionNames = new Set(selectedSections.map(({ name }) => name));
    const rowIds = new Set<number>(
      selectedSections.flatMap(({ rows }) => rows.map(({ id }) => id))
    );

    return (
      sales?.filter(({ seating }) => {
        return (
          (seating?.sectionId && sectionIds.has(seating?.sectionId)) ||
          (seating?.section && sectionNames.has(seating?.section)) ||
          (seating?.rowId && rowIds.has(seating.rowId))
        );
      }) ?? null
    );
  }, [hasNoFilter, sales, selectedSections]);

  const salesSummary = useMemo(() => {
    if (!hasTableTotalsFeature) {
      return null;
    }
    if (hasNoFilter) {
      return entities?.salesSummary;
    }

    // If there's client-side filtering, the best we can do is aggregate the sales and skip those with different currency
    return createAggregatedSale(filteredEventSales);
  }, [
    hasNoFilter,
    filteredEventSales,
    entities?.salesSummary,
    hasTableTotalsFeature,
  ]);

  const salesAccordionItem = useMemo(
    () =>
      accordionItemProps &&
      translateEventToSaleAccordionItem(accordionItemProps, 0, true),
    [accordionItemProps]
  );

  const saleIds = filteredEventSales?.map(({ id }) => id) ?? [];

  const { BodyComponent, AfterHeaderComponent } = salesAccordionItem || {};

  const noData =
    (!isLoading && !salesAccordionItem) || (!isItemsLoading && !sales);
  return (
    <>
      <div className={styles.sidePanelToolbar}>
        <SidePanelToolbar
          sectionType={SectionType.SalesSideTable}
          filterBar={<SaleSideTableFilterBar />}
          entityIds={hasSelectedEntities ? saleIds : undefined}
          noData={noData}
        />
        {!noData && AfterHeaderComponent && <AfterHeaderComponent />}
      </div>
      {isLoading && <PosSpinner />}
      {noData ? (
        <NoData>
          <FormatContent
            id={FormatContentId.NoDataAvailable}
            params={<Content id={ContentId.Sales} />}
          />
        </NoData>
      ) : (
        BodyComponent &&
        accordionItemProps && (
          <BodyComponent
            event={accordionItemProps.event}
            entities={filteredEventSales ?? []}
            entitiesSummary={salesSummary}
            entityCount={accordionItemProps.counts.salesCnt ?? 0}
            useTableNavKeys={true}
            useVirtuoso
            withOuterPadding={false}
            disablePagination={true}
            isSideTable={true}
            initState={{
              sorting: [
                {
                  id: SalesTableColumnId.OrderDate,
                  desc: true,
                },
              ],
            }}
          />
        )
      )}
    </>
  );
};
export const SalesSideTable = ({
  viagVirtId,
  selectedSections,
  queryInput,
}: {
  viagVirtId: string;
  selectedSections?: SectionInfo[];
  queryInput?: Partial<Pick<SaleQuery, 'purchaseOrderIds' | 'listingIds'>>;
}) => {
  const { activeAccountWebClientConfig } = useAppContext();

  const { trackError } = useClientLogContext();

  const getCatalogDataExpandedCallback = useCallback(
    (ids: string[], filterQuery: SaleQuery) =>
      getSalesForEvents(
        ids,
        filterQuery,
        false,
        {
          activeAccountWebClientConfig,
          onError: (error) => {
            trackError('SaleClient.getSalesForEvents', error, {
              ...filterQuery,
              eventIds: ids,
            });
          },
        },
        true
      ),
    [activeAccountWebClientConfig, trackError]
  );

  const getActivePosEntity = useCallback(
    async (saleId: number) => {
      if (activeAccountWebClientConfig.activeAccountId) {
        const saleDetail = await new SaleClient(
          activeAccountWebClientConfig
        ).getSaleBySaleId(saleId);

        if (saleDetail) {
          return {
            posEntityId: saleDetail.id,
            posEntity: saleDetail,
            posEntityDisplayId: saleDetail.idOnMkp,
          };
        }
      }
      return {};
    },
    [activeAccountWebClientConfig]
  );

  return (
    <BulkEditHubContextProvider>
      <FilterQueryContextProvider<SaleQuery>
        initialQuery={{
          ...DefaultSaleQuery,
          eventOrMappingIds: [viagVirtId],
        }}
        emptyQuery={EmptySaleQuery}
        viewModeSettingId={UserSetting.SalePageViewMode}
        loadQueryFromUrl={false}
      >
        <CatalogDataContextProvider<SaleQuery>
          entityType={ActionOutboxEntityType.Sale}
          queryKey={'getCatalogForSale'}
          doNotLogPageLoad
          getCatalogDataExpanded={getCatalogDataExpandedCallback}
          transformEventData={transformData}
        >
          <CatalogMultiSelectionContextProvider type={'sale'}>
            <MultiSelectionContextProvider>
              <ActivePosEntityProvider<SaleDetails>
                entityType={ActionOutboxEntityType.Sale}
                getActivePosEntity={getActivePosEntity}
                doNotLogPageLoad
              >
                <ActiveSortTableColumnProvider
                  entityType={ActionOutboxEntityType.Sale}
                  defaultSorting={[]}
                >
                  <DialogProvider>
                    <Sales
                      viagVirtId={viagVirtId}
                      selectedSections={selectedSections}
                      queryInput={queryInput}
                    />
                  </DialogProvider>
                </ActiveSortTableColumnProvider>
              </ActivePosEntityProvider>
            </MultiSelectionContextProvider>
          </CatalogMultiSelectionContextProvider>
        </CatalogDataContextProvider>
      </FilterQueryContextProvider>
    </BulkEditHubContextProvider>
  );
};
